import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createBrowserSpeechRecognizer,
  SpeechRecognizerLike,
} from './browserSpeechRecognizer';

import { management_api_host } from '@/utils/api';
import { getAuthorization } from '@/utils/authorization-util';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { detectLocalIntent, LocalIntentResponse } from './localIntent';

import {
  AssistantChatMessage,
  AssistantControllerApi,
  AssistantState,
  NavigationState,
  PageContext,
  PlaybackVoiceDecision,
  PPTAssistantCapability,
  PPTModelCandidate,
  PPTQuestionCapability,
  StartConversationOptions,
  VoiceListeningMode,
  VoiceSession,
} from './types';

/** 小车导航错误码 → 中文提示 */

/** =========================
 * Initial state
 * ========================= */

const defaultPageContext: PageContext = {
  pathname: '/',
  pageType: 'other',
};

const initialState: AssistantState = {
  mode: 'idle',
  pageContext: defaultPageContext,
  voiceSession: null,

  listening: false,
  speaking: false,

  transcript: '',
  panelOpen: false,
  lastAssistantReply: '',

  pendingAction: null,
  mediaState: {
    kind: 'none',
    playing: false,
    paused: false,
    overlayOpen: false,
  },

  voiceListeningMode: 'none',

  pptQuestionMode: false,
  awaitingPptQuestion: false,
  askingPptQuestion: false,
  pendingQuestionModelId: undefined,
  pendingQuestionModelLabel: undefined,
  pptChatHistory: [],

  navigationState: { status: 'idle' },
};

const AssistantControllerContext = createContext<AssistantControllerApi | null>(
  null,
);

type Props = {
  children: React.ReactNode;
  recognitionLang?: string;
  ttsApi?: string;
  useLocalIntent?: boolean;
  localIntentApi?: string;
};

/** =========================
 * Provider
 * ========================= */

export function AssistantControllerProvider({
  children,
  recognitionLang = 'zh-CN',
  ttsApi = `${management_api_host}/tts/speak`,
  useLocalIntent = true,
  localIntentApi = `${management_api_host}/assistant/local-intent`,
}: Props) {
  const [state, setState] = useState<AssistantState>(initialState);

  const recognizerRef = useRef<SpeechRecognizerLike | null>(null);
  const navigatorRef = useRef<((path: string) => void) | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioAbortRef = useRef<AbortController | null>(null);
  const currentAudioUrlRef = useRef<string | null>(null);

  const pptCapabilityRef = useRef<PPTAssistantCapability | null>(null);
  const shouldKeepListeningRef = useRef(false);
  const stateRef = useRef<AssistantState>(initialState);

  const pptQuestionCapabilityRef = useRef<PPTQuestionCapability | null>(null);

  const lastSpokenTextRef = useRef('');
  const lastSpokenAtRef = useRef(0);
  const ttsCooldownUntilRef = useRef(0);
  const ignoreQuestionTranscriptUntilRef = useRef(0);
  const handleTranscriptAbortRef = useRef<AbortController | null>(null);
  type SpeechQueueItem = {
    text: string;
    onDone?: () => void;
  };
  type SpeechChunk = {
    id: number;
    text: string;
    url?: string;
    error?: boolean;
    loading: boolean;
    onDone?: () => void;
  };

  const speechChunkSeqRef = useRef(0);
  const speechChunksRef = useRef<SpeechChunk[]>([]);
  const speechPlayingIndexRef = useRef(0);
  const speechPrefetchingRef = useRef(false);
  const speechQueueRef = useRef<SpeechQueueItem[]>([]);
  const speechQueueRunningRef = useRef(false);
  const streamSentenceBufferRef = useRef('');
  const lastQuestionModelIdRef = useRef<string | null>(null);
  const pendingModelPromptIdRef = useRef(0);

  const transcriptCommitTimerRef = useRef<number | null>(null);
  const muteCurrentAnswerPlaybackRef = useRef(false);

  const holdToTalkActiveRef = useRef(false);
  const holdToTalkStartedAtRef = useRef(0);
  const holdToTalkCancelRef = useRef(false);

  // 小车导航相关
  const navigationAbortRef = useRef<AbortController | null>(null);
  const navigationPollTimerRef = useRef<number | null>(null);
  // 优先使用用户配置的 robot_server_url，否则 fallback 到环境变量
  // 使用 useQuery 订阅 userInfo 缓存变化（不触发额外请求，enabled=false 禁止自动 fetch）
  // 注意: TanStack Query v5 即使 enabled:false 也要求提供 queryFn，此处用占位函数，
  // 真正的数据获取由 useFetchUserInfo (user-setting-hooks.tsx) 负责填充 ['userInfo'] 缓存。
  const { data: cachedUserInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: () => ({}),
    enabled: false, // 不自动请求，只订阅缓存更新
    initialData: {},
  });
  const _rawRobotUrl =
    (cachedUserInfo as any)?.robot_server_url ||
    process.env.VITE_ROBOT_SERVER_URL ||
    '';
  const ROBOT_HOST = _rawRobotUrl
    ? _rawRobotUrl.startsWith('http')
      ? _rawRobotUrl
      : `http://${_rawRobotUrl}`
    : '';
  // 缓存小车服务是否可达，避免每次都探测
  const robotServerAvailableRef = useRef<boolean | null>(null);
  // 缓存小车航点列表
  const waypointsRef = useRef<Array<{ name: string }>>([]);

  /** 统一名称格式：全角转半角、NFKC 标准化、小写，用于点位匹配容错 */
  const normalizeName = (name: string): string => {
    let result = name.trim();
    // 全角字符转半角 (U+FF01 ~ U+FF5E)
    result = result.replace(/[\uFF01-\uFF5E]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
    );
    // 全角空格
    result = result.replace(/\u3000/g, ' ');
    // NFKC 标准化
    result = result.normalize('NFKC');
    return result.toLowerCase();
  };

  /** 检查点位是否存在（全角/半角/大小写容错匹配） */
  const checkPointExists = (pointName: string): boolean => {
    const normalized = normalizeName(pointName);
    return waypointsRef.current.some(
      (wp) => normalizeName(wp.name) === normalized,
    );
  };

  /** 直接调用小车 API 的封装，支持超时和外部 AbortSignal */
  const robotFetch = async <T = any,>(
    path: string,
    options: RequestInit & { timeout?: number } = {},
  ): Promise<T> => {
    const { timeout = 5000, signal: externalSignal, ...fetchOpts } = options;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // 如果外部也传了 signal，任一 abort 都应终止请求
    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener('abort', onExternalAbort);

    try {
      const resp = await fetch(`${ROBOT_HOST}${path}`, {
        ...fetchOpts,
        signal: controller.signal,
      });
      if (!resp.ok) {
        throw new Error(`Robot API ${path} returned ${resp.status}`);
      }
      return resp.json();
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }
  };

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  /** =========================
   * Panel
   * ========================= */
  const openOverlay = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mediaState: {
        ...prev.mediaState,
        overlayOpen: true,
      },
    }));
  }, []);

  const toggleOverlay = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mediaState: {
        ...prev.mediaState,
        overlayOpen: !prev.mediaState.overlayOpen,
      },
    }));
  }, []);
  const openPanel = useCallback(() => {
    const current = stateRef.current;
    // ❗播放中禁止打开面板
    if (current.mediaState.kind === 'ppt-video') {
      console.log('[openPanel called]', stateRef.current);
      return;
    }

    setState((prev) => ({ ...prev, panelOpen: true }));
  }, []);

  const closePanel = useCallback(() => {
    setState((prev) => ({ ...prev, panelOpen: false }));
  }, []);

  const togglePanel = useCallback(() => {
    setState((prev) => ({ ...prev, panelOpen: !prev.panelOpen }));
  }, []);
  const setVoiceListeningMode = useCallback((mode: VoiceListeningMode) => {
    setState((prev) => ({
      ...prev,
      voiceListeningMode: mode,
    }));
  }, []);

  /** =========================
   * Navigation
   * ========================= */

  const requestNavigate = useCallback((path: string): boolean => {
    const nav = navigatorRef.current;
    if (nav) {
      nav(path);
      return true;
    }
    return false;
  }, []);

  const bindNavigator = useCallback((navigate: (path: string) => void) => {
    navigatorRef.current = navigate;
  }, []);

  /** =========================
   * PPT capability
   * ========================= */

  const registerPPTCapability = useCallback(
    (cap: PPTAssistantCapability | null) => {
      pptCapabilityRef.current = cap;
    },
    [],
  );

  const registerPPTQuestionCapability = useCallback(
    (cap: PPTQuestionCapability | null) => {
      pptQuestionCapabilityRef.current = cap;
    },
    [],
  );

  /** =========================
   * Audio stop
   * ========================= */

  const stopSpeech = useCallback(() => {
    try {
      audioAbortRef.current?.abort();
    } catch {}

    audioAbortRef.current = null;

    const audio = audioRef.current;

    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.onended = null;
        audio.onerror = null;
        audio.src = '';
      } catch {}
    }

    if (currentAudioUrlRef.current) {
      try {
        URL.revokeObjectURL(currentAudioUrlRef.current);
      } catch {}

      currentAudioUrlRef.current = null;
    }

    for (const chunk of speechChunksRef.current) {
      if (chunk.url) {
        try {
          URL.revokeObjectURL(chunk.url);
        } catch {}
      }
    }

    speechChunksRef.current = [];
    speechPlayingIndexRef.current = 0;
    speechQueueRunningRef.current = false;
    speechQueueRef.current = [];

    // 关键重置：确保后续 enqueueSpeech 不受上一次状态污染
    muteCurrentAnswerPlaybackRef.current = false;
    streamSentenceBufferRef.current = '';
    audioRef.current = null; // 强制下次播放创建全新 Audio 元素，避免 src='' 后复用导致无声

    setState((prev) => ({
      ...prev,
      speaking: false,
    }));
  }, []);

  const setPPTPlaybackOpen = useCallback(
    (payload: {
      open: boolean;
      modelId?: string;
      modelLabel?: string;
      playing?: boolean;
      paused?: boolean;
      keepOverlayOpen?: boolean;
    }) => {
      setState((prev) => ({
        ...prev,
        mediaState: payload.open
          ? {
              kind: 'ppt-video',
              playing: payload.playing ?? prev.mediaState.playing ?? false,
              paused: payload.paused ?? prev.mediaState.paused ?? false,
              currentModelId: payload.modelId ?? prev.mediaState.currentModelId,
              currentModelLabel:
                payload.modelLabel ?? prev.mediaState.currentModelLabel,

              // 核心修改：不要无脑关闭面板
              overlayOpen: payload.keepOverlayOpen
                ? true
                : (prev.mediaState.overlayOpen ?? false),
            }
          : {
              kind: 'none',
              playing: false,
              paused: false,
              overlayOpen: false,
            },

        voiceListeningMode: payload.open ? 'command-only' : 'none',
      }));
    },
    [],
  );

  const makeMsgId = () =>
    `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const appendPptChatMessage = useCallback(
    (msg: Omit<AssistantChatMessage, 'id' | 'createdAt'>) => {
      const fullMsg: AssistantChatMessage = {
        id: makeMsgId(),
        createdAt: Date.now(),
        ...msg,
      };

      setState((prev) => ({
        ...prev,
        pptChatHistory: [...prev.pptChatHistory, fullMsg],
      }));

      return fullMsg.id;
    },
    [],
  );

  const clearPPTPlayback = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mediaState: {
        kind: 'none',
        playing: false,
        paused: false,
        overlayOpen: false,
      },
      voiceListeningMode: stateRef.current.voiceSession?.active
        ? 'command-only'
        : 'none',
      pptQuestionMode: false,
      awaitingPptQuestion: false,
      askingPptQuestion: false,
      pendingQuestionModelId: undefined,
      pendingQuestionModelLabel: undefined,
    }));
  }, []);

  /** =========================
   * TTS
   * ========================= */

  const fetchTtsAudioUrl = useCallback(
    async (text: string, signal?: AbortSignal): Promise<string> => {
      const token = getAuthorization();

      const resp = await fetch(ttsApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token } : {}),
        },
        signal,
        body: JSON.stringify({
          text,
          format: 'mp3',
        }),
      });

      if (!resp.ok) {
        throw new Error(`TTS http ${resp.status}`);
      }

      const blob = await resp.blob();

      if (!blob || blob.size === 0) {
        throw new Error('TTS returned empty audio');
      }

      return URL.createObjectURL(blob);
    },
    [ttsApi],
  );
  const playPreparedSpeechQueue = useCallback(() => {
    if (speechQueueRunningRef.current) return;

    console.log(
      '[SpeechQueue] start, chunks:',
      speechChunksRef.current.length,
      'muted:',
      muteCurrentAnswerPlaybackRef.current,
    );

    speechQueueRunningRef.current = true;

    const playNext = async () => {
      if (muteCurrentAnswerPlaybackRef.current) {
        console.warn('[SpeechQueue] muted, stopping queue');
        speechQueueRunningRef.current = false;
        return;
      }

      const chunk = speechChunksRef.current[speechPlayingIndexRef.current];

      if (!chunk) {
        speechQueueRunningRef.current = false;
        setState((prev) => ({
          ...prev,
          speaking: false,
        }));
        return;
      }

      // 等当前 chunk 的 TTS 准备好；最多等 8 秒，避免卡死
      const startWait = Date.now();

      while (!chunk.url && !chunk.error) {
        if (Date.now() - startWait > 8000) {
          chunk.error = true;
          break;
        }

        await new Promise((r) => window.setTimeout(r, 80));
      }

      // 这个 chunk 失败就跳过，不阻塞后续
      if (chunk.error || !chunk.url) {
        speechPlayingIndexRef.current += 1;
        chunk.onDone?.();
        void playNext();
        return;
      }

      let audio = audioRef.current;

      if (!audio) {
        audio = new Audio();
        audio.preload = 'auto';
        audioRef.current = audio;
      }

      currentAudioUrlRef.current = chunk.url;
      audio.src = chunk.url;

      setState((prev) => ({
        ...prev,
        speaking: true,
        listening: false,
      }));

      audio.onended = () => {
        try {
          URL.revokeObjectURL(chunk.url!);
        } catch {}

        if (currentAudioUrlRef.current === chunk.url) {
          currentAudioUrlRef.current = null;
        }

        chunk.onDone?.();

        speechPlayingIndexRef.current += 1;
        void playNext();
      };

      audio.onerror = () => {
        try {
          URL.revokeObjectURL(chunk.url!);
        } catch {}

        chunk.onDone?.();

        speechPlayingIndexRef.current += 1;
        void playNext();
      };

      try {
        console.log('[SpeechQueue] audio.play()', chunk.url?.slice(0, 60));
        await audio.play();
      } catch (playErr) {
        console.warn('[SpeechQueue] audio.play() FAILED:', playErr);
        speechPlayingIndexRef.current += 1;
        void playNext();
      }
    };

    void playNext();
  }, []);
  const clearTranscriptCommitTimer = useCallback(() => {
    if (transcriptCommitTimerRef.current !== null) {
      window.clearTimeout(transcriptCommitTimerRef.current);
      transcriptCommitTimerRef.current = null;
    }
  }, []);

  const scheduleTranscriptCommit = useCallback(
    (silenceMs: number) => {
      clearTranscriptCommitTimer();

      transcriptCommitTimerRef.current = window.setTimeout(() => {
        transcriptCommitTimerRef.current = null;

        const recognizer = recognizerRef.current;
        if (!recognizer) return;

        const finalText = recognizer
          .consumeFinalText({ includeInterim: true })
          .trim();

        if (!finalText) return;

        try {
          recognizer.stop();
        } catch {}

        setState((prev) => ({
          ...prev,
          transcript: '',
        }));

        void handleTranscriptRef.current(finalText);
      }, silenceMs);
    },
    [clearTranscriptCommitTimer],
  );

  const startRecognizer = useCallback(() => {
    const current = stateRef.current;

    if (current.speaking) return;
    if (current.voiceListeningMode === 'none') return;

    const silenceMs = current.pptQuestionMode ? 5000 : 1500;

    let rec = recognizerRef.current;

    if (!rec) {
      try {
        rec = createBrowserSpeechRecognizer({
          lang: recognitionLang,
          silenceMs,
        });
      } catch (err) {
        console.warn('[Assistant] recognizer init failed:', err);
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '当前浏览器不支持语音识别',
          listening: false,
        }));
        return;
      }

      recognizerRef.current = rec;

      rec.onresult = (event) => {
        const recognizer = recognizerRef.current;
        const preview =
          event.interimText || event.finalText || recognizer?.getText?.() || '';

        setState((prev) => ({
          ...prev,
          transcript: preview.trim(),
        }));

        // 关键：每次有识别结果，都由"外部"重新计时决定什么时候提交
        if (!holdToTalkActiveRef.current) {
          if (!stateRef.current.pptQuestionMode) {
            scheduleTranscriptCommit(1500);
          }
        }
      };

      rec.onerror = (err: any) => {
        console.warn('[Assistant] SpeechRecognition error:', err);

        clearTranscriptCommitTimer();

        const errorCode = err?.error || '';
        const isPermissionDenied =
          errorCode === 'not-allowed' || errorCode === 'service-not-allowed';

        // 权限被拒绝：彻底停止，不再自动重试
        if (isPermissionDenied) {
          shouldKeepListeningRef.current = false;

          try {
            recognizerRef.current?.destroy?.();
          } catch {}

          recognizerRef.current = null;

          setState((prev) => ({
            ...prev,
            listening: false,
            speaking: false,
            voiceSession: prev.voiceSession
              ? { ...prev.voiceSession, active: false }
              : prev.voiceSession,
            voiceListeningMode: 'none',
            lastAssistantReply:
              '麦克风权限未开启，请在浏览器中允许麦克风访问后再试。',
          }));

          return;
        }

        // 其它错误：保留原来的可恢复逻辑
        try {
          recognizerRef.current?.destroy?.();
        } catch {}

        recognizerRef.current = null;

        setState((prev) => ({
          ...prev,
          listening: false,
        }));

        window.setTimeout(() => {
          const current = stateRef.current;

          const canRestart =
            shouldKeepListeningRef.current &&
            current.mode === 'conversation' &&
            !!current.voiceSession?.active &&
            !current.speaking &&
            !current.listening;

          if (!canRestart) return;

          startRecognizer();
        }, 500);
      };

      rec.onend = () => {
        setState((prev) => ({
          ...prev,
          listening: false,
        }));

        window.setTimeout(() => {
          const current = stateRef.current;

          const canRestart =
            shouldKeepListeningRef.current &&
            holdToTalkActiveRef.current &&
            !current.speaking &&
            !current.listening;

          if (!canRestart) return;

          startRecognizer();
        }, 250);
      };
    }

    rec.start();

    setState((prev) => ({
      ...prev,
      listening: true,
    }));
  }, [clearTranscriptCommitTimer, recognitionLang, scheduleTranscriptCommit]);

  const forceStartListening = useCallback(() => {
    clearTranscriptCommitTimer();

    try {
      recognizerRef.current?.stop?.();
    } catch {}

    setState((prev) => ({
      ...prev,
      listening: false,
      speaking: false,
    }));

    window.setTimeout(() => {
      startRecognizer();
    }, 200);
  }, [clearTranscriptCommitTimer, startRecognizer]);

  const speakText = useCallback(
    async (text: string, onEnd?: () => void) => {
      // stopSpeech();
      clearTranscriptCommitTimer();
      try {
        recognizerRef.current?.stop?.();
        recognizerRef.current?.destroy?.();
      } catch {}
      recognizerRef.current = null;

      const clean = text.trim();
      if (!clean) {
        onEnd?.();
        return;
      }

      lastSpokenTextRef.current = clean;
      lastSpokenAtRef.current = Date.now();

      const token = getAuthorization();
      const ctrl = new AbortController();
      audioAbortRef.current = ctrl;

      setState((prev) => ({
        ...prev,
        speaking: true,
        listening: false,
        lastAssistantReply: clean,
      }));

      try {
        const resp = await fetch(ttsApi, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          signal: ctrl.signal,
          body: JSON.stringify({
            text: clean,
            format: 'mp3',
          }),
        });

        if (!resp.ok) {
          throw new Error(`TTS http ${resp.status}`);
        }

        const blob = await resp.blob();
        if (!blob || blob.size === 0) {
          throw new Error('TTS returned empty audio');
        }

        const url = URL.createObjectURL(blob);
        currentAudioUrlRef.current = url;

        let audio = audioRef.current;
        if (!audio) {
          audio = new Audio();
          audio.preload = 'auto';
          audioRef.current = audio;
        }

        audio.src = url;

        audio.onended = () => {
          if (currentAudioUrlRef.current) {
            try {
              URL.revokeObjectURL(currentAudioUrlRef.current);
            } catch {}
            currentAudioUrlRef.current = null;
          }

          ttsCooldownUntilRef.current = Date.now() + 500;

          setState((prev) => ({
            ...prev,
            speaking: false,
          }));

          onEnd?.();
        };

        audio.onerror = () => {
          if (currentAudioUrlRef.current) {
            try {
              URL.revokeObjectURL(currentAudioUrlRef.current);
            } catch {}
            currentAudioUrlRef.current = null;
          }

          setState((prev) => ({
            ...prev,
            speaking: false,
          }));

          onEnd?.();
        };

        await audio.play();
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.warn('[Assistant] tts speak failed:', err);
        }

        if (currentAudioUrlRef.current) {
          try {
            URL.revokeObjectURL(currentAudioUrlRef.current);
          } catch {}
          currentAudioUrlRef.current = null;
        }

        setState((prev) => ({
          ...prev,
          speaking: false,
          lastAssistantReply: clean,
        }));

        onEnd?.();
      } finally {
        audioAbortRef.current = null;
      }
    },
    [clearTranscriptCommitTimer, stopSpeech, ttsApi],
  );

  const enqueueSpeech = useCallback(
    (text: string, onDone?: () => void) => {
      const clean = text.trim();
      if (!clean) return;

      console.log(
        '[SpeechQueue] enqueueSpeech:',
        clean.slice(0, 40),
        'queueRunning:',
        speechQueueRunningRef.current,
      );

      const id = ++speechChunkSeqRef.current;

      const chunk: SpeechChunk = {
        id,
        text: clean,
        loading: true,
        onDone,
      };

      speechChunksRef.current.push(chunk);

      const ctrl = new AbortController();
      audioAbortRef.current = ctrl;

      // 并行请求 TTS，不等播放
      void fetchTtsAudioUrl(clean, ctrl.signal)
        .then((url) => {
          chunk.url = url;
          chunk.loading = false;
          playPreparedSpeechQueue();
        })
        .catch((e) => {
          if (e?.name !== 'AbortError') {
            console.warn('[Assistant] TTS chunk failed:', e);
          }

          chunk.error = true;
          chunk.loading = false;
          playPreparedSpeechQueue();
        });

      // 播放器如果还没启动，立即启动等待第一个 chunk
      playPreparedSpeechQueue();
    },
    [fetchTtsAudioUrl, playPreparedSpeechQueue],
  );

  const flushSpeakableSentences = useCallback((delta: string) => {
    streamSentenceBufferRef.current += delta;

    const text = streamSentenceBufferRef.current;
    const parts = text.split(/(?<=[。！？!?\n])/);

    if (parts.length <= 1) return [];

    streamSentenceBufferRef.current = parts.pop() || '';
    return parts.map((x) => x.trim()).filter(Boolean);
  }, []);
  /** =========================
   * Global stop
   * ========================= */

  const stopAllVoice = useCallback(
    (reason?: string) => {
      console.log('[Assistant] stopAllVoice:', reason);

      shouldKeepListeningRef.current = false;

      recognizerRef.current?.destroy?.();
      recognizerRef.current = null;
      stopSpeech();

      setState((prev) => ({
        ...prev,
        listening: false,
        speaking: false,
        transcript: '',
      }));
    },
    [stopSpeech],
  );

  const stopConversation = useCallback(
    (reason?: string) => {
      console.log('[Assistant] stopConversation:', reason);
      stopAllVoice(reason);

      setState((prev) => ({
        ...prev,
        mode: 'idle',
        voiceSession: null,
        pendingAction: null,
        voiceListeningMode: 'none',
        awaitingPptQuestion: false,
        askingPptQuestion: false,
        pendingQuestionModelId: undefined,
        pendingQuestionModelLabel: undefined,
      }));
    },
    [stopAllVoice],
  );

  const stopListeningOnly = useCallback(() => {
    clearTranscriptCommitTimer();

    // 关键：禁止 onend 自动重启
    shouldKeepListeningRef.current = false;
    holdToTalkActiveRef.current = false;

    try {
      recognizerRef.current?.stop?.();
      recognizerRef.current?.destroy?.();
    } catch {}

    recognizerRef.current = null;

    try {
      recognizerRef.current?.consumeFinalText?.({ includeInterim: true });
    } catch {}

    setState((prev) => ({
      ...prev,
      listening: false,
      transcript: '',
      voiceListeningMode:
        prev.mediaState.kind === 'ppt-video' ? 'command-only' : 'none',
    }));
  }, [clearTranscriptCommitTimer]);

  /** =========================
   * Recognizer
   * ========================= */

  const handleTranscriptRef = useRef<(text: string) => Promise<void>>(
    async () => {},
  );

  /** =========================
   * Helpers
   * ========================= */

  const normalizeForCompare = (s: string) =>
    (s || '')
      .toLowerCase()
      .replace(/[，。！？、；：""''（）()【】\[\]\-—_.,!?;:'"`~]/g, '')
      .replace(/\s+/g, '')
      .trim();

  const calcBigramDice = (a: string, b: string) => {
    if (!a || !b) return 0;
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) {
      return a === b ? 1 : 0;
    }

    const grams = (s: string) => {
      const arr: string[] = [];
      for (let i = 0; i < s.length - 1; i += 1) {
        arr.push(s.slice(i, i + 2));
      }
      return arr;
    };

    const aGrams = grams(a);
    const bGrams = grams(b);

    const aCount = new Map<string, number>();
    for (const g of aGrams) {
      aCount.set(g, (aCount.get(g) || 0) + 1);
    }

    let overlap = 0;
    for (const g of bGrams) {
      const n = aCount.get(g) || 0;
      if (n > 0) {
        overlap += 1;
        aCount.set(g, n - 1);
      }
    }

    return (2 * overlap) / (aGrams.length + bGrams.length);
  };

  const shouldIgnoreAsSelfSpeech = (rawText: string) => {
    const now = Date.now();
    const lastText = lastSpokenTextRef.current || '';

    if (!rawText.trim() || !lastText.trim()) return false;

    // 只在"最近刚播报过"的短时间窗口内做过滤
    const deltaMs = now - lastSpokenAtRef.current;
    if (deltaMs > 8000) return false;

    const input = normalizeForCompare(rawText);
    const spoken = normalizeForCompare(lastText);

    if (!input || !spoken) return false;

    // 太短的识别结果不做相似度过滤，避免误杀系统命令
    if (input.length < 4) return false;

    // 1) 完全一致
    if (input === spoken) return true;

    // 2) 输入是播报内容中的长片段
    if (input.length >= 6 && spoken.includes(input)) return true;

    // 3) 播报内容是输入中的长片段（识别多带了点头尾）
    if (spoken.length >= 6 && input.includes(spoken)) return true;

    // 4) 用 bigram Dice 做近似匹配
    const sim = calcBigramDice(input, spoken);
    if (sim >= 0.72) return true;

    return false;
  };

  const normalizeCommand = useCallback(
    (text: string) => text.trim().replace(/\s+/g, ''),
    [],
  );

  const resolveNavigateCommand = useCallback(
    (text: string) => {
      const t = normalizeCommand(text);

      if (
        [
          '跳转聊天',
          '跳转聊天页',
          '打开聊天',
          '去聊天页',
          '进入聊天',
          '打开对话',
        ].includes(t)
      ) {
        return {
          path: '/chat',
          reply: '好的，正在为你打开聊天页面',
        };
      }

      if (['跳转知识库', '打开知识库', '去知识库', '进入知识库'].includes(t)) {
        return {
          path: '/knowledge',
          reply: '好的，正在为你打开知识库',
        };
      }
      if (['跳转PPT', '打开ppt', '去ppt', '进入ppt'].includes(t)) {
        return {
          path: '/write',
          reply: '好的，正在为你打开ppt页面',
        };
      }

      return null;
    },
    [normalizeCommand],
  );

  const isPPTExplainCommand = useCallback(
    (text: string) => {
      const t = normalizeCommand(text);
      return [
        '讲解ppt',
        '讲解PPT',
        '播放讲解',
        '播放ppt',
        '播放PPT',
        '讲解产品',
        '讲解这个产品',
        '播放这个型号',
        '开始讲解',
      ].includes(t);
    },
    [normalizeCommand],
  );

  const askForModelSelection = useCallback(
    (candidates: PPTModelCandidate[]) => {
      setState((prev) => ({
        ...prev,

        pendingAction: {
          type: 'PLAY_PPT_MODEL',
          candidates,
          source: 'voice-followup',
        },
        mediaState: {
          ...prev.mediaState,
          overlayOpen: true,
        },
      }));

      const reply = '讲解哪个型号？';
      const promptId = Date.now();
      pendingModelPromptIdRef.current = promptId;

      void speakText(reply, () => {
        if (pendingModelPromptIdRef.current !== promptId) return;

        stopListeningOnly();

        // setState((prev) => ({
        //   ...prev,
        //   listening: false,
        //   transcript: '',
        //   voiceListeningMode: 'command-only',
        // }));
      });
    },
    [stopListeningOnly, speakText],
  );

  /**
   * 请求用户选择要介绍的产品型号（类似 askForModelSelection）
   * 触发条件：用户说"介绍产品"但未指定具体型号
   */
  const askForProductIntroduction = useCallback(
    (candidates: PPTModelCandidate[]) => {
      setState((prev) => ({
        ...prev,
        pendingAction: {
          type: 'INTRODUCE_PRODUCT',
          candidates,
          source: 'voice-followup',
        },
        mediaState: {
          ...prev.mediaState,
          overlayOpen: true,
        },
      }));

      const reply = '介绍哪个型号？';
      const promptId = Date.now();
      pendingModelPromptIdRef.current = promptId;

      void speakText(reply, () => {
        if (pendingModelPromptIdRef.current !== promptId) return;
        stopListeningOnly();
      });
    },
    [stopListeningOnly, speakText],
  );

  /** =========================
   * 小车导航 + 产品介绍
   * ========================= */

  const stopNavigationPolling = useCallback(() => {
    if (navigationPollTimerRef.current) {
      window.clearInterval(navigationPollTimerRef.current);
      navigationPollTimerRef.current = null;
    }
  }, []);

  const cancelNavigation = useCallback(() => {
    stopNavigationPolling();
    if (navigationAbortRef.current) {
      navigationAbortRef.current.abort();
      navigationAbortRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      navigationState: { status: 'idle' },
    }));
    // 通知小车取消当前导航
    if (ROBOT_HOST) {
      fetch(`${ROBOT_HOST}/cmd/cancel_nav`, { method: 'POST' }).catch(() => {});
    }
  }, [stopNavigationPolling, ROBOT_HOST]);

  /**
   * 检测小车导航服务是否可达，同时获取航点列表
   * 探测成功后缓存航点列表和可用状态
   */
  const checkRobotServerAvailable = useCallback(async (): Promise<boolean> => {
    if (!ROBOT_HOST) {
      robotServerAvailableRef.current = false;
      console.log('[Robot] VITE_ROBOT_SERVER_URL 未配置，跳过导航');
      return false;
    }
    if (robotServerAvailableRef.current === false) return false;
    if (robotServerAvailableRef.current === true) return true;

    try {
      // 获取航点列表作为探测（同时缓存航点）
      const data = await robotFetch<{ waypoints: Array<{ name: string }> }>(
        '/reeman/position',
        { timeout: 3000 },
      );
      waypointsRef.current = data.waypoints || [];
      robotServerAvailableRef.current = true;
      console.log(
        '[Robot] 导航服务可达，获取到',
        waypointsRef.current.length,
        '个航点',
      );
      return true;
    } catch {
      robotServerAvailableRef.current = false;
      console.log('[Robot] 导航服务不可达，将跳过导航直接播放PPT讲解视频');
      return false;
    }
  }, [ROBOT_HOST]);

  const playProductPptVideo = useCallback(
    async (modelId: string, modelLabel: string) => {
      const cap = pptCapabilityRef.current;

      stopSpeech();
      stopListeningOnly();

      setState((prev) => ({
        ...prev,
        pendingAction: null,
        transcript: '',
        listening: false,
        panelOpen: false,
        lastAssistantReply: `正在播放：${modelLabel}`,
        pptQuestionMode: false,
        awaitingPptQuestion: false,
        askingPptQuestion: false,
        pendingQuestionModelId: undefined,
        pendingQuestionModelLabel: undefined,
        voiceListeningMode: 'command-only',
        mediaState: {
          ...prev.mediaState,
          overlayOpen: false,
        },
      }));

      if (!cap?.playModelById) {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '当前不在可讲解的PPT页面',
        }));
        return;
      }

      try {
        await Promise.resolve(cap.playModelById(modelId, modelLabel));
      } catch (err: any) {
        console.warn('[Robot] PPT讲解视频播放失败:', err?.message);
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '讲解视频暂时无法播放，请稍后再试',
        }));
      }
    },
    [stopListeningOnly, stopSpeech],
  );

  /**
   * 核心流程：直接调用小车 API → 发送导航命令 → 前端轮询导航状态 → 到达后自动播放产品 PPT 讲解视频
   *
   * 直接对接 ROBOT_HOST，不再经过中间服务：
   * 1. GET  /reeman/position        → 获取航点列表（探测时已缓存）
   * 2. POST /cmd/nav_name { point } → 发送导航命令（立即返回）
   * 3. GET  /reeman/nav_status        → 前端轮询导航状态
   *    res=0 空闲, 1 准备中, 2 导航中, 3 到达, 4 取消, 6 错误
   * 4. POST /cmd/cancel_nav            → 取消导航
   *
   * 流程：
   * - 探测成功 + 点位存在 → "跟我来" → 开始导航 → 到达后播放 PPT 讲解视频
   * - 探测成功但点位不存在 → 直接播放 PPT 讲解视频
   * - 探测失败 → 直接播放 PPT 讲解视频
   */
  const navigateAndIntroduce = useCallback(
    async (modelId: string, modelLabel: string) => {
      // 先检测小车导航服务是否可达（同时获取航点列表）
      const serverAvailable = await checkRobotServerAvailable();

      // 服务不可达 → 直接播放 PPT 讲解视频
      if (!serverAvailable) {
        void playProductPptVideo(modelId, modelLabel);
        return;
      }

      // 检查目标点位是否存在
      const pointExists = checkPointExists(modelId);
      if (!pointExists) {
        console.warn('[Robot] 目标点未找到:', modelId, '直接播放PPT讲解视频');
        void playProductPptVideo(modelId, modelLabel);
        return;
      }

      // 清理上一次导航
      cancelNavigation();

      const abortController = new AbortController();
      navigationAbortRef.current = abortController;

      const updateNavState = (navState: NavigationState) => {
        setState((prev) => ({
          ...prev,
          navigationState: navState,
        }));
      };

      try {
        // ---- 阶段 1: 说"跟我来"，启动导航 ----
        updateNavState({
          status: 'navigating',
          modelId,
          modelLabel,
          message: '正在前往目的地',
        });

        setState((prev) => ({
          ...prev,
          panelOpen: true,
          lastAssistantReply: `跟我来，带您前往${modelLabel}的位置`,
        }));

        // 说"跟我来"
        enqueueSpeech(`跟我来，带您前往${modelLabel}的位置`);

        // 发送导航命令
        try {
          const navResp = await robotFetch<{ status?: string }>(
            '/cmd/nav_name',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ point: modelId }),
              timeout: 5000,
              signal: abortController.signal,
            },
          );
          // 检查返回体：如果小车明确返回失败，降级
          if (navResp.status && navResp.status !== 'success') {
            console.warn('[Robot] 导航命令被小车拒绝:', navResp);
            updateNavState({ status: 'idle' });
            void playProductPptVideo(modelId, modelLabel);
            return;
          }
          console.log('[Robot] 导航命令已发送，目标:', modelId);
        } catch (err: any) {
          // 被主动取消不算异常
          if (abortController.signal.aborted) return;
          console.warn('[Robot] 发送导航命令失败:', err?.message);
          updateNavState({ status: 'idle' });
          void playProductPptVideo(modelId, modelLabel);
          return;
        }

        // ---- 阶段 2: 前端轮询导航状态 ----
        const NAV_TIMEOUT = 300; // 5分钟超时
        const POLL_INTERVAL = 1000; // 1秒轮询
        const MAX_CONSECUTIVE_FAILURES = 5; // 连续失败5次才报错（容许偶尔500）
        const startTime = Date.now();
        let consecutiveFailures = 0;
        let navStarted = false; // 是否已进入导航状态（看到过 res=6/1/2）
        let navResult: { status: 'success' | 'error'; message: string } = {
          status: 'error',
          message: '导航超时',
        };

        // 导航错误码映射（res=6 时 reason 为负数 -1~-9）
        const NAV_ERROR_MESSAGES: Record<number, string> = {
          [-1]: '正在对接充电桩',
          [-2]: '急停按钮被按下',
          [-3]: '适配器充电中',
          [-4]: '目标点未找到',
          [-5]: 'AGV对接失败',
          [-6]: '定位异常',
          [-7]: '固定路线点太远',
          [-8]: '固定路线未找到',
          [-9]: '读取点位信息失败',
        };

        while (Date.now() - startTime < NAV_TIMEOUT * 1000) {
          if (abortController.signal.aborted) {
            navResult = { status: 'error', message: '导航已取消' };
            break;
          }

          try {
            const status = await robotFetch<{
              res: number;
              reason: number;
            }>('/reeman/nav_status', {
              timeout: 3000,
              signal: abortController.signal,
            });
            const { res, reason } = status;
            consecutiveFailures = 0; // 成功一次即重置

            // 准备阶段错误
            if (res === 6 && reason !== 0) {
              const msg = NAV_ERROR_MESSAGES[reason] || `未知错误(${reason})`;
              navResult = { status: 'error', message: msg };
              console.warn('[Robot] 导航准备错误:', msg);
              break;
            }

            // 标记导航已启动（看到 res=6/1/2 任一状态）
            if (res === 6 || res === 1 || res === 2) {
              navStarted = true;
            }

            // 导航取消
            if (res === 4) {
              navResult = { status: 'error', message: '导航已取消' };
              break;
            }

            // 到达目标 (res=3, reason=0)
            // 必须先看到过导航状态(6/1/2)才算真到达，避免发指令前 res=3 误判
            if (navStarted && res === 3 && reason === 0) {
              navResult = {
                status: 'success',
                message: '导航完成',
              };
              console.log('[Robot] 已到达目标点');
              break;
            }

            // 导航失败 (res=3, reason!=0 且已启动过导航)
            if (navStarted && res === 3 && reason !== 0) {
              navResult = { status: 'error', message: '导航失败' };
              console.warn('[Robot] 导航失败: res=3, reason=', reason);
              break;
            }
          } catch (err: any) {
            // 被主动取消
            if (abortController.signal.aborted) {
              navResult = { status: 'error', message: '导航已取消' };
              break;
            }
            // nav_status 偶尔 500 或网络抖动，累积失败次数
            consecutiveFailures++;
            console.warn(
              `[Robot] nav_status 查询失败(${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`,
              err?.message,
            );
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
              navResult = { status: 'error', message: '导航状态查询连续失败' };
              break;
            }
          }

          // 等待轮询间隔
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        }

        if (abortController.signal.aborted) return;

        if (navResult.status === 'success') {
          // ---- 导航成功，到达目的地，播放 PPT 讲解视频 ----
          updateNavState({
            status: 'arrived',
            modelId,
            modelLabel,
            message: '已到达目的地',
          });

          setState((prev) => ({
            ...prev,
            lastAssistantReply: `已到达${modelLabel}的位置，正在播放讲解视频`,
          }));

          void playProductPptVideo(modelId, modelLabel);
        } else {
          // ---- 导航失败，降级为直接播放 PPT 讲解视频 ----
          const errorMsg = navResult.message || '导航失败';
          console.warn('[Robot] 导航失败，直接播放PPT讲解视频:', errorMsg);
          updateNavState({ status: 'idle' });

          void playProductPptVideo(modelId, modelLabel);
        }
      } catch (err: any) {
        if (abortController.signal.aborted) return;

        // 所有异常统一降级为直接播放 PPT 讲解视频
        updateNavState({ status: 'idle' });
        console.warn('[Robot] 导航异常，直接播放PPT讲解视频:', err?.message);
        void playProductPptVideo(modelId, modelLabel);
      }
    },
    [
      cancelNavigation,
      checkRobotServerAvailable,
      enqueueSpeech,
      playProductPptVideo,
      ROBOT_HOST,
    ],
  );

  /**
   * 执行播放模型（从候选列表选择后）
   */
  const executePlayModel = useCallback(
    (id: string, label: string) => {
      setState((prev) => ({
        ...prev,
        pendingAction: null,
        transcript: '',
        listening: false,
        lastAssistantReply: `正在播放：${label}`,
        mediaState: {
          ...prev.mediaState,
          overlayOpen: false,
        },
      }));

      const cap = pptCapabilityRef.current;
      if (!cap?.playModelById) return;

      void Promise.resolve(cap.playModelById(id, label)).finally(() => {
        window.setTimeout(() => {
          stopListeningOnly();
          setState((prev) => ({
            ...prev,
            pendingAction: null,
            listening: false,
            transcript: '',
            voiceListeningMode: 'command-only',
            mediaState: {
              ...prev.mediaState,
              overlayOpen: false,
            },
          }));
        }, 0);
      });
    },
    [stopListeningOnly],
  );
  const clearPendingAction = useCallback(() => {
    setState((prev) => ({
      ...prev,
      pendingAction: null,
    }));
  }, []);

  const selectPendingModel = useCallback(
    (id: string) => {
      let matchedId: string | null = null;
      let matchedLabel: string | null = null;
      let actionType: string | null = null;

      setState((prev) => {
        const pending = prev.pendingAction;
        if (!pending) return prev;

        const matched = pending.candidates.find((x) => x.id === id);
        if (!matched) return prev;

        matchedId = matched.id;
        matchedLabel = matched.label;
        actionType = pending.type;

        return {
          ...prev,
          // 产品介绍流程需要保持面板打开，播放模式才关闭
          panelOpen: actionType === 'INTRODUCE_PRODUCT' ? true : false,
          pendingAction: null,
          transcript: '',
          mediaState: {
            ...prev.mediaState,
            overlayOpen: false,
          },
        };
      });

      if (!matchedId || !matchedLabel || !actionType) return;

      // 关键：先打断"请问讲解/介绍哪个型号"的播报
      stopSpeech();

      window.setTimeout(() => {
        if (actionType === 'PLAY_PPT_MODEL') {
          executePlayModel(matchedId!, matchedLabel!);
        } else if (actionType === 'INTRODUCE_PRODUCT') {
          // 选择型号后，由 navigateAndIntroduce 内部判断是否有小车服务
          void navigateAndIntroduce(matchedId!, matchedLabel!);
        }
      }, 0);
    },
    [executePlayModel, navigateAndIntroduce, stopSpeech],
  );

  const chineseNumberMap: Record<string, number> = {
    一: 1,
    二: 2,
    两: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    七: 7,
    八: 8,
    九: 9,
    十: 10,
  };

  const parseSelectionIndex = (text: string): number | null => {
    const clean = text
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[，。,.！!？?]/g, '');

    // 1 / 第1个 / 选1 / 播放1号
    const digitMatch = clean.match(
      /(?:第|选|选择|播放|讲解)?(\d+)(?:个|号|项|款)?/,
    );
    if (digitMatch) {
      const n = Number(digitMatch[1]);
      return Number.isFinite(n) && n > 0 ? n - 1 : null;
    }

    // 第一个 / 选第二个 / 播放三号
    const cnMatch = clean.match(
      /(?:第|选|选择|播放|讲解)?([一二两三四五六七八九十])(?:个|号|项|款)?/,
    );
    if (cnMatch) {
      const n = chineseNumberMap[cnMatch[1]];
      return n ? n - 1 : null;
    }

    return null;
  };

  const normalizeModelText = (v: string) =>
    v
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[，。,.！!？?]/g, '')
      .replace(/^(播放|讲解|选择|选|我要看|我要听|打开)/, '')
      .replace(/(这个|这一款|这款|型号)$/g, '');

  const resolvePendingModelSelection = useCallback(
    (text: string) => {
      const pending = stateRef.current.pendingAction;

      if (
        !pending ||
        (pending.type !== 'PLAY_PPT_MODEL' &&
          pending.type !== 'INTRODUCE_PRODUCT')
      )
        return false;

      const raw = text.trim();
      const normalized = normalizeModelText(raw);

      // 匹配后的执行动作
      const handleMatch = (id: string, label: string) => {
        // 先清除 pendingAction，保持面板打开
        setState((prev) => ({
          ...prev,
          pendingAction: null,
          panelOpen:
            pending.type === 'INTRODUCE_PRODUCT' ? true : prev.panelOpen,
        }));

        if (pending.type === 'PLAY_PPT_MODEL') {
          executePlayModel(id, label);
        } else if (pending.type === 'INTRODUCE_PRODUCT') {
          void navigateAndIntroduce(id, label);
        }
      };

      // 1. 优先匹配序号
      const index = parseSelectionIndex(raw);

      if (index !== null) {
        const candidate = pending.candidates[index];

        if (candidate) {
          handleMatch(candidate.id, candidate.label);
          return true;
        }

        void speakText(
          `没有第 ${index + 1} 个型号，请重新选择，或者直接点击列表中的型号`,
          () => {
            stopListeningOnly();
          },
        );

        return true;
      }

      // 2. 再匹配型号名称
      const matched = pending.candidates.find((x) => {
        const label = normalizeModelText(x.label);
        const id = normalizeModelText(x.id);

        return (
          label === normalized ||
          id === normalized ||
          label.includes(normalized) ||
          normalized.includes(label) ||
          id.includes(normalized) ||
          normalized.includes(id)
        );
      });

      if (!matched) {
        void speakText(
          '我没有匹配到这个型号，请再说一次，或者直接点击列表中的型号',
          () => {
            stopListeningOnly();
          },
        );

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          voiceListeningMode: 'command-only',
          lastAssistantReply:
            '我没有匹配到这个型号，请再说一次，或者直接点击列表中的型号',
        }));

        return true;
      }

      handleMatch(matched.id, matched.label);
      return true;
    },
    [executePlayModel, navigateAndIntroduce, speakText, stopListeningOnly],
  );

  const enterPPTQuestionMode = useCallback(
    (options?: { source?: 'voice' | 'button' }) => {
      const source = options?.source || 'voice';

      const cap = pptQuestionCapabilityRef.current;
      const current = cap?.getCurrentPlayingModel();

      if (!cap || !current) {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '当前没有正在播放的讲解内容，无法提问',
        }));
        return;
      }

      const askable = cap?.canAskQuestion?.();
      if (askable && !askable.ok) {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: askable.reason || '当前无法进入提问模式',
        }));
        return;
      }

      if (lastQuestionModelIdRef.current !== current.id) {
        setState((prev) => ({
          ...prev,
          pptChatHistory: [],
        }));
        lastQuestionModelIdRef.current = current.id;
      }

      cap.pausePlayback();

      clearTranscriptCommitTimer();

      try {
        const recognizer = recognizerRef.current;
        recognizer?.stop?.();

        // 清空底层已累计但尚未提交的识别内容
        recognizer?.consumeFinalText?.({ includeInterim: true });
      } catch {}

      setState((prev) => ({
        ...prev,
        listening: false,
        pptQuestionMode: true,
        awaitingPptQuestion: true,
        askingPptQuestion: false,
        pendingQuestionModelId: current.id,
        pendingQuestionModelLabel: current.label,
        transcript: '',
        voiceListeningMode: 'free-question',
        lastAssistantReply:
          source === 'voice'
            ? '已进入提问模式，请直接提问'
            : '已进入提问模式，请开始提问',
        mediaState: {
          ...prev.mediaState,
          playing: false,
          paused: true,
          overlayOpen: true,
          currentModelId: current.id,
          currentModelLabel: current.label,
        },
      }));

      appendPptChatMessage({
        role: 'system',
        content: `已进入 ${current.label} 的提问模式`,
        status: 'done',
      });

      // 关键：刚进入提问模式时，给一点时间忽略播放尾音/切换残留
      ignoreQuestionTranscriptUntilRef.current = Date.now() + 1200;
      setState((prev) => ({
        ...prev,
        transcript: '',
        listening: false,
      }));
    },
    [appendPptChatMessage, clearTranscriptCommitTimer, forceStartListening],
  );

  const isPPTQuestionIntent = (text: string) => {
    const clean = text.replace(/[，。！？、,.!?]/g, '').replace(/\s+/g, '');

    const exactPatterns = [
      /提问/,
      /请问/,
      /我想提问/,
      /我要提问/,
      /我有问题/,
      /有问题/,
      /问一下/,
      /提个问/,
    ];

    return exactPatterns.some((re) => re.test(clean));
  };
  const handlePlaybackVoiceCommand = useCallback(
    (text: string): PlaybackVoiceDecision => {
      const clean = text.trim();
      const cap = pptCapabilityRef.current;

      if (!clean || !cap) return 'pass';

      const current = stateRef.current;

      if (current.mediaState.kind !== 'ppt-video') {
        return 'pass';
      }

      // 注意：现在不再通过语音"提问"自动进入监听
      // 因为监听只能由按住说话 / 快捷键触发
      // 所以这里不要再处理 isPPTQuestionIntent

      if (/^(暂停|暂停播放|停一下|先停一下)$/.test(clean)) {
        try {
          cap.setPlaybackDucking?.(false);
        } catch {}

        cap.pausePlayback?.();

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          speaking: false,
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          voiceListeningMode: 'command-only',
          lastAssistantReply: '已暂停播放',
          mediaState:
            prev.mediaState.kind === 'ppt-video'
              ? {
                  ...prev.mediaState,
                  playing: false,
                  paused: true,
                  overlayOpen: true,
                }
              : prev.mediaState,
        }));

        return 'handled';
      }

      if (/^(继续|继续播放|恢复播放|接着播放)$/.test(clean)) {
        try {
          cap.setPlaybackDucking?.(false);
        } catch {}

        cap.resumePlayback?.();

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          speaking: false,
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          voiceListeningMode: 'command-only',
          lastAssistantReply: '已继续播放',
          mediaState:
            prev.mediaState.kind === 'ppt-video'
              ? {
                  ...prev.mediaState,
                  playing: true,
                  paused: false,
                  overlayOpen: false,
                }
              : prev.mediaState,
        }));

        return 'handled';
      }

      if (
        /^(关闭播放|退出播放|停止播放|结束播放|关闭视频|退出讲解|关闭讲解|退出)$/.test(
          clean,
        )
      ) {
        try {
          cap.setPlaybackDucking?.(false);
        } catch {}

        cap.stopPlayback?.();
        cap.closePlayback?.();

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          speaking: false,
          lastAssistantReply: '已关闭播放',
          voiceListeningMode: 'command-only',
          mediaState: {
            kind: 'none',
            playing: false,
            paused: false,
            overlayOpen: false,
          },
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          pendingQuestionModelId: undefined,
          pendingQuestionModelLabel: undefined,
          pptChatHistory: [],
        }));

        return 'handled';
      }

      // 播放视频时，不是播放控制命令，就交给 PPT 提问逻辑
      return 'pass';
    },
    [],
  );

  const submitPPTQuestion = useCallback(
    async (question: string) => {
      const clean = question.trim();
      if (!clean) return;

      const modelId =
        stateRef.current.pendingQuestionModelId ||
        stateRef.current.mediaState.currentModelId;

      const cap = pptQuestionCapabilityRef.current;

      if (!cap || !modelId) {
        setState((prev) => ({
          ...prev,
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          lastAssistantReply: '当前没有可提问的PPT讲解内容',
        }));
        return;
      }

      stopSpeech();
      muteCurrentAnswerPlaybackRef.current = false;
      streamSentenceBufferRef.current = '';
      speechQueueRef.current = [];
      speechChunksRef.current = [];
      speechPlayingIndexRef.current = 0;
      speechQueueRunningRef.current = false;

      appendPptChatMessage({
        role: 'user',
        content: clean,
        status: 'done',
      });

      const assistantMsgId = makeMsgId();

      setState((prev) => ({
        ...prev,
        pptQuestionMode: true,
        awaitingPptQuestion: false,
        askingPptQuestion: true,
        transcript: '',
        lastAssistantReply: '',
        pptChatHistory: [
          ...prev.pptChatHistory,
          {
            id: assistantMsgId,
            role: 'assistant',
            content: '',
            status: 'streaming',
            createdAt: Date.now(),
          },
        ],
      }));

      try {
        if (cap.askQuestionStream) {
          let fullText = '';

          await cap.askQuestionStream({
            model: modelId,
            question: clean,
            onDelta: (delta) => {
              fullText += delta;

              setState((prev) => ({
                ...prev,
                lastAssistantReply: fullText,
                pptChatHistory: prev.pptChatHistory.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        content: fullText,
                        status: muteCurrentAnswerPlaybackRef.current
                          ? 'done'
                          : 'streaming',
                      }
                    : m,
                ),
              }));

              if (!muteCurrentAnswerPlaybackRef.current) {
                const chunks = flushSpeakableSentences(delta);
                for (const chunk of chunks) {
                  enqueueSpeech(chunk);
                }
              }
            },
            onDone: (finalText) => {
              const finalAnswer =
                (finalText || fullText || '').trim() || '暂时没有获取到答案。';
              const remain = streamSentenceBufferRef.current.trim();

              const finishQuestionTurn = () => {
                setState((prev) => ({
                  ...prev,
                  pptQuestionMode: true,
                  askingPptQuestion: false,
                  awaitingPptQuestion: true,
                  voiceListeningMode: 'free-question',
                  transcript: '',
                  lastAssistantReply: finalAnswer,
                  pptChatHistory: prev.pptChatHistory.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, content: finalAnswer, status: 'done' }
                      : m,
                  ),
                }));
                streamSentenceBufferRef.current = '';
              };

              if (muteCurrentAnswerPlaybackRef.current) {
                finishQuestionTurn();
              } else if (remain) {
                enqueueSpeech(remain, finishQuestionTurn);
              } else {
                finishQuestionTurn();
              }
            },
            onError: (message) => {
              const msg = message || '流式提问失败';
              setState((prev) => ({
                ...prev,
                pptQuestionMode: true,
                askingPptQuestion: false,
                awaitingPptQuestion: true,
                voiceListeningMode: 'free-question',
                transcript: '',
                lastAssistantReply: msg,
                pptChatHistory: prev.pptChatHistory.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, content: msg, status: 'error' }
                    : m,
                ),
              }));
            },
          });

          return;
        }

        const answer = await cap.askQuestion?.({
          model: modelId,
          question: clean,
        });

        const finalAnswer = (answer || '').trim() || '暂时没有获取到答案。';

        setState((prev) => ({
          ...prev,
          pptQuestionMode: true,
          askingPptQuestion: true,
          awaitingPptQuestion: false,
          voiceListeningMode: 'free-question',
          lastAssistantReply: finalAnswer,
          pptChatHistory: prev.pptChatHistory.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: finalAnswer, status: 'done' }
              : m,
          ),
        }));

        void speakText(finalAnswer, () => {
          setState((prev) => ({
            ...prev,
            pptQuestionMode: true,
            askingPptQuestion: false,
            awaitingPptQuestion: true,
            voiceListeningMode: 'free-question',
            transcript: '',
            lastAssistantReply: finalAnswer,
          }));
        });
      } catch (e: any) {
        const msg = e?.message || '提问失败，请稍后重试';

        setState((prev) => ({
          ...prev,
          pptQuestionMode: true,
          askingPptQuestion: false,
          awaitingPptQuestion: true,
          voiceListeningMode: 'free-question',
          transcript: '',
          lastAssistantReply: msg,
          pptChatHistory: prev.pptChatHistory.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: msg, status: 'error' }
              : m,
          ),
        }));

        void speakText(msg, () => {});
      }
    },
    [
      appendPptChatMessage,
      enqueueSpeech,
      flushSpeakableSentences,
      forceStartListening,
      speakText,
    ],
  );

  const startHoldToTalkPPTQuestion = useCallback(() => {
    const current = stateRef.current;

    // 正在生成回答时不能打断，因为还没有完整回答
    if (current.askingPptQuestion && !current.speaking) {
      message.info('正在生成回答，请稍后再说');
      return;
    }

    // 正在播报回答时，允许打断
    if (current.askingPptQuestion && current.speaking) {
      try {
        stopSpeakingOnly();
      } catch {}

      try {
        stopSpeech();
      } catch {}

      try {
        window.speechSynthesis?.cancel?.();
      } catch {}

      try {
        speechQueueRef.current = [];
      } catch {}

      try {
        muteCurrentAnswerPlaybackRef.current = true;
      } catch {}

      setState((prev) => ({
        ...prev,
        speaking: false,
        transcript: '',
        lastAssistantReply: prev.lastAssistantReply,
      }));
    }

    clearTranscriptCommitTimer();

    shouldKeepListeningRef.current = true;
    holdToTalkActiveRef.current = true;
    holdToTalkCancelRef.current = false;
    holdToTalkStartedAtRef.current = Date.now();

    // 1. 停止所有播报，避免 TTS 被识别进去
    try {
      stopSpeech();
    } catch {}

    try {
      speechQueueRef.current = [];
    } catch {}

    try {
      muteCurrentAnswerPlaybackRef.current = true;
    } catch {}

    // 2. 如果正在播放 PPT 视频，先暂停视频，避免视频声音进入识别
    if (current.mediaState.kind === 'ppt-video') {
      try {
        pptCapabilityRef.current?.pausePlayback?.();
      } catch {}

      try {
        pptCapabilityRef.current?.setPlaybackDucking?.(true);
      } catch {}
    }

    // 3. 清空旧识别缓存
    try {
      recognizerRef.current?.stop?.();
      recognizerRef.current?.consumeFinalText?.({ includeInterim: true });
    } catch {}

    const shouldUsePptQuestion =
      current.mediaState.kind === 'ppt-video' || current.pptQuestionMode;

    setState((prev) => ({
      ...prev,
      mode: 'conversation',
      transcript: '',
      listening: true,
      speaking: false,
      pendingAction: prev.pendingAction,

      // 如果当前有 PPT 视频，则按住说话直接进入 PPT 提问模式
      pptQuestionMode: shouldUsePptQuestion ? true : prev.pptQuestionMode,
      awaitingPptQuestion: shouldUsePptQuestion
        ? true
        : prev.awaitingPptQuestion,
      askingPptQuestion: false,

      voiceListeningMode: shouldUsePptQuestion
        ? 'free-question'
        : 'command-only',
      lastAssistantReply: shouldUsePptQuestion
        ? '请按住说话，松开发送'
        : '请按住说话，松开发送',
    }));

    window.setTimeout(() => {
      try {
        startRecognizer();
      } catch {}
    }, 0);
  }, [clearTranscriptCommitTimer, startRecognizer, stopSpeech]);

  const buildLocalIntentScene = useCallback(() => {
    const current = stateRef.current;
    const pending = current.pendingAction;

    const candidates =
      pending?.type === 'PLAY_PPT_MODEL' ||
      pending?.type === 'INTRODUCE_PRODUCT'
        ? pending.candidates
        : [];

    return {
      pageType: current.pageContext.pageType,
      inPptPlayback: current.mediaState.kind === 'ppt-video',
      pptQuestionMode: current.pptQuestionMode,
      awaitingPptQuestion: current.awaitingPptQuestion,
      candidates: candidates.map((x) => ({
        id: x.id,
        label: x.label,
      })),
    };
  }, []);

  const cancelPPTQuestionMode = useCallback(() => {
    clearTranscriptCommitTimer();

    try {
      recognizerRef.current?.stop?.();
    } catch {}

    setState((prev) => ({
      ...prev,
      listening: false,
      pptQuestionMode: false,
      awaitingPptQuestion: false,
      askingPptQuestion: false,
      pendingQuestionModelId: undefined,
      pendingQuestionModelLabel: undefined,
      transcript: '',
      lastAssistantReply: '已退出提问模式',
      voiceListeningMode:
        prev.mediaState.kind === 'ppt-video' ? 'command-only' : 'none',
      mediaState: {
        ...prev.mediaState,
        overlayOpen: true,
      },
    }));

    // window.setTimeout(() => {
    //   forceStartListening();
    // }, 200);
  }, [clearTranscriptCommitTimer, forceStartListening]);

  const executeLocalIntent = useCallback(
    async (intent: LocalIntentResponse, rawText: string) => {
      const name = intent.command?.name;
      const args = intent.command?.args || {};
      const reply = (intent.reply || '').trim();

      if (intent.kind === 'unknown' && !looksMeaningless(rawText)) {
        const finalReply = await runLocalChat(rawText);

        setState((prev) => ({
          ...prev,
          lastAssistantReply: finalReply,
        }));

        void speakText(finalReply, () => {
          const current = stateRef.current;
          const canRestart =
            shouldKeepListeningRef.current &&
            !current.speaking &&
            !current.listening &&
            ((current.mode === 'conversation' &&
              !!current.voiceSession?.active) ||
              current.pptQuestionMode);

          if (canRestart) {
            forceStartListening();
          }
        });

        return true;
      }

      if (intent.kind === 'chat') {
        const finalReply = await runLocalChat(rawText);

        setState((prev) => ({
          ...prev,
          lastAssistantReply: finalReply,
        }));

        void speakText(finalReply, () => {
          const current = stateRef.current;
          const canRestart =
            shouldKeepListeningRef.current &&
            !current.speaking &&
            !current.listening &&
            ((current.mode === 'conversation' &&
              !!current.voiceSession?.active) ||
              current.pptQuestionMode);

          if (canRestart) {
            forceStartListening();
          }
        });

        return true;
      }

      if (
        intent.kind !== 'command' ||
        !name ||
        (intent.confidence ?? 0) < 0.65
      ) {
        return false;
      }

      if (name === 'NAVIGATE_CHAT') {
        requestNavigate('/chat');
        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '好的，正在为你打开聊天页面',
        }));
        return true;
      }

      if (name === 'NAVIGATE_KNOWLEDGE') {
        requestNavigate('/knowledge');
        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '好的，正在为你打开知识库',
        }));
        return true;
      }

      if (name === 'NAVIGATE_PPT') {
        requestNavigate('/write');
        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '好的，正在为你打开PPT页面',
        }));
        return true;
      }

      if (name === 'PLAY_PPT') {
        const cap = pptCapabilityRef.current;

        if (!cap) {
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '当前不在可讲解的PPT页面',
          }));
          return true;
        }

        const candidates = cap.getModelCandidates();

        if (!candidates.length) {
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '当前页面没有可选型号',
          }));
          return true;
        }

        if (candidates.length === 1) {
          executePlayModel(candidates[0].id, candidates[0].label);
          return true;
        }

        askForModelSelection(candidates);
        return true;
      }

      if (name === 'PAUSE_PLAYBACK') {
        pptCapabilityRef.current?.pausePlayback?.();
        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '已暂停播放',
          voiceListeningMode: 'command-only',
          mediaState: {
            ...prev.mediaState,
            playing: false,
            paused: true,
            overlayOpen: true,
          },
        }));
        return true;
      }

      if (name === 'RESUME_PLAYBACK') {
        pptCapabilityRef.current?.resumePlayback?.();
        setState((prev) => ({
          ...prev,
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          transcript: '',
          panelOpen: false,
          voiceListeningMode: 'command-only',
          lastAssistantReply: reply || '已继续播放',
          mediaState: {
            ...prev.mediaState,
            playing: true,
            paused: false,
            overlayOpen: false,
          },
        }));
        // forceStartListening();
        return true;
      }

      if (name === 'STOP_PLAYBACK') {
        pptCapabilityRef.current?.stopPlayback?.();
        pptCapabilityRef.current?.closePlayback?.();

        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '已关闭播放',
          voiceListeningMode: 'command-only',
          mediaState: {
            kind: 'none',
            playing: false,
            paused: false,
            overlayOpen: false,
          },
          pptQuestionMode: false,
          awaitingPptQuestion: false,
          askingPptQuestion: false,
          pendingQuestionModelId: undefined,
          pendingQuestionModelLabel: undefined,
        }));
        return true;
      }

      if (name === 'ENTER_PPT_QUESTION') {
        enterPPTQuestionMode({ source: 'voice' });
        return true;
      }

      if (name === 'EXIT_PPT_QUESTION') {
        cancelPPTQuestionMode();
        return true;
      }

      if (name === 'STOP_CONVERSATION') {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: reply || '好的，已结束语音对话',
        }));
        stopConversation('local-intent-stop');
        return true;
      }

      if (name === 'SELECT_MODEL') {
        const rawLabel = String(args?.model_label || rawText || '').trim();
        if (!rawLabel) return false;
        return resolvePendingModelSelection(rawLabel);
      }

      if (name === 'INTRODUCE_PRODUCT') {
        const rawLabel = String(args?.model_label || rawText || '').trim();

        const cap = pptCapabilityRef.current;
        if (!cap) return false;
        const candidates = cap.getModelCandidates();
        if (candidates.length === 0) return false;

        // 未指定型号 → 弹出候选列表让用户选择
        if (!rawLabel) {
          askForProductIntroduction(candidates);
          return true;
        }

        // 尝试匹配已知型号
        const match = candidates.find(
          (c) =>
            c.id === rawLabel ||
            c.label === rawLabel ||
            c.label.includes(rawLabel) ||
            rawLabel.includes(c.label),
        );

        if (match) {
          void navigateAndIntroduce(match.id, match.label);
        } else if (candidates.length === 1) {
          // 只有一个候选，直接用
          void navigateAndIntroduce(candidates[0].id, candidates[0].label);
        } else {
          // 匹配不到 → 弹出候选列表
          askForProductIntroduction(candidates);
        }
        return true;
      }

      if (name === 'NAVIGATE_AND_INTRODUCE') {
        const rawLabel = String(args?.model_label || rawText || '').trim();

        const cap = pptCapabilityRef.current;
        if (!cap) return false;
        const candidates = cap.getModelCandidates();
        if (candidates.length === 0) return false;

        // "跟我来"但不带型号 → 弹出候选列表
        if (!rawLabel) {
          askForProductIntroduction(candidates);
          return true;
        }

        // 尝试匹配已知型号
        const match = candidates.find(
          (c) =>
            c.id === rawLabel ||
            c.label === rawLabel ||
            c.label.includes(rawLabel) ||
            rawLabel.includes(c.label),
        );

        if (match) {
          void navigateAndIntroduce(match.id, match.label);
        } else if (candidates.length === 1) {
          void navigateAndIntroduce(candidates[0].id, candidates[0].label);
        } else {
          askForProductIntroduction(candidates);
        }
        return true;
      }

      return false;
    },
    [
      askForModelSelection,
      askForProductIntroduction,
      cancelPPTQuestionMode,
      enterPPTQuestionMode,
      executePlayModel,
      forceStartListening,
      navigateAndIntroduce,
      requestNavigate,
      resolvePendingModelSelection,
      speakText,
      stopConversation,
    ],
  );

  const VALID_ROUTE_PATHS = new Set(['/', '/chat', '/knowledge', '/write']);

  const normalizePath = (v: string) =>
    (v || '').trim().replace(/\/+$/, '') || '/';

  const isValidRoutePath = (path: string) => {
    const normalized = normalizePath(path);
    return VALID_ROUTE_PATHS.has(normalized);
  };

  const handleTranscript = useCallback(
    async (text: string) => {
      console.log('[Voice Debug]', {
        text,
        mode: stateRef.current.mode,
        voiceListeningMode: stateRef.current.voiceListeningMode,
        mediaState: stateRef.current.mediaState,
        voiceSession: stateRef.current.voiceSession,
      });
      setState((prev) => ({
        ...prev,
        mode: 'conversation',
        listening: false,
        speaking: false,
        transcript: '',
        lastAssistantReply: '思考中...',
      }));
      const clean = text.trim();
      if (!clean) return;

      handleTranscriptAbortRef.current?.abort();
      const controller = new AbortController();
      handleTranscriptAbortRef.current = controller;

      if (shouldIgnoreAsSelfSpeech(clean)) {
        console.log('[Assistant] ignored self-speech-like transcript:', clean);
        return;
      }

      console.log('[Assistant] final transcript:', clean);

      // 0. 如果正在等PPT问题，则本句直接作为问题提交
      const inPptQuestionMode =
        stateRef.current.pptQuestionMode &&
        stateRef.current.awaitingPptQuestion;
      const now = Date.now();

      if (inPptQuestionMode) {
        if (now < ignoreQuestionTranscriptUntilRef.current) {
          console.log(
            '[Assistant] ignore residual transcript in ppt question mode:',
            clean,
          );
          return;
        }

        // 仍然允许这些明确控制命令
        const nav = resolveNavigateCommand(clean);

        if (nav) {
          const current = stateRef.current;

          // PPT 问答模式中禁止页面跳转
          if (
            current.mediaState.kind === 'ppt-video' ||
            current.pptQuestionMode ||
            current.awaitingPptQuestion
          ) {
            setState((prev) => ({
              ...prev,
              listening: false,
              transcript: '',
              lastAssistantReply: '请先结束当前讲解或提问',
            }));

            return;
          }

          // 检查路由是否合法
          if (!isValidRoutePath(nav.path)) {
            setState((prev) => ({
              ...prev,
              listening: false,
              transcript: '',
              lastAssistantReply: `没有找到对应页面：${nav.path}`,
            }));

            return;
          }

          const changed = requestNavigate(nav.path);

          setState((prev) => ({
            ...prev,
            listening: false,
            transcript: '',
            lastAssistantReply: changed ? nav.reply : '当前已经在该页面',
          }));

          return;
        }

        if (/^(继续|继续播放|恢复播放)$/.test(clean)) {
          pptCapabilityRef.current?.resumePlayback?.();
          setState((prev) => ({
            ...prev,
            pptQuestionMode: false,
            awaitingPptQuestion: false,
            askingPptQuestion: false,
            transcript: '',
            lastAssistantReply: '已继续播放',
            voiceListeningMode: 'command-only',
            mediaState: {
              ...prev.mediaState,
              playing: true,
              paused: false,
              overlayOpen: false,
            },
          }));
          return;
        }

        if (/^(暂停|暂停播放)$/.test(clean)) {
          pptCapabilityRef.current?.pausePlayback?.();
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '已暂停播放',
            voiceListeningMode: 'free-question',
            mediaState: {
              ...prev.mediaState,
              playing: false,
              paused: true,
              overlayOpen: false,
            },
          }));
          return;
        }

        if (/^(关闭播放|停止播放|结束播放)$/.test(clean)) {
          pptCapabilityRef.current?.stopPlayback?.();
          pptCapabilityRef.current?.closePlayback?.();
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '已关闭播放',
            voiceListeningMode: 'none',
            transcript: '',
            mediaState: {
              kind: 'none',
              playing: false,
              paused: false,
              overlayOpen: false,
            },
            pptQuestionMode: false,
            awaitingPptQuestion: false,
            askingPptQuestion: false,
            pendingQuestionModelId: undefined,
            pendingQuestionModelLabel: undefined,
          }));
          return;
        }

        if (/^(停止提问|退出提问|结束提问|取消提问)$/.test(clean)) {
          setState((prev) => ({
            ...prev,
            pptQuestionMode: false,
            awaitingPptQuestion: false,
            askingPptQuestion: false,
            transcript: '',
            lastAssistantReply: '已退出提问模式',
            voiceListeningMode: 'command-only',
          }));
          return;
        }

        if (/^(停止对话|关闭语音|退出语音|结束对话|停止语音)$/.test(clean)) {
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '好的，已结束语音对话',
          }));
          stopConversation('voice-command-stop');
          return;
        }

        // 关键：PPT提问模式下，不按住按钮时，不允许自动发给后端
        if (!holdToTalkActiveRef.current) {
          console.log(
            '[Assistant] ignore free speech in ppt question mode without hold-to-talk:',
            clean,
          );
          return;
        }

        await submitPPTQuestion(clean);
        return;
      }

      const currentNow = stateRef.current;

      // 视频播放中：禁止 pending 型号选择抢优先级
      if (currentNow.mediaState.kind === 'ppt-video') {
        const nav = resolveNavigateCommand(clean);
        if (nav) {
          requestNavigate(nav.path);
          setState((prev) => ({
            ...prev,
            pendingAction: null,
            lastAssistantReply: nav.reply,
          }));
          return;
        }

        const playbackDecision = handlePlaybackVoiceCommand(clean);
        if (playbackDecision === 'handled') return;
        if (playbackDecision === 'blocked') return;

        setState((prev) => ({
          ...prev,
          pendingAction: null,
          pptQuestionMode: true,
          awaitingPptQuestion: true,
          askingPptQuestion: true,
          lastAssistantReply: '正在生成回答...',
        }));

        await submitPPTQuestion(clean);
        return;
      }

      // 非播放中，才允许 pending 型号选择
      if (resolvePendingModelSelection(clean)) return;

      const nav = resolveNavigateCommand(clean);
      if (nav) {
        requestNavigate(nav.path);

        setState((prev) => ({
          ...prev,
          lastAssistantReply: nav.reply,
        }));
        return;
      }

      // 3. 播放页控制命令
      const playbackDecision = handlePlaybackVoiceCommand(clean);
      if (playbackDecision === 'handled') return;
      if (playbackDecision === 'blocked') return;

      // 4. 通用停止
      if (/^(停止对话|关闭语音|退出语音|结束对话|停止语音)$/.test(clean)) {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '好的，已结束语音对话',
        }));
        stopConversation('voice-command-stop');
        return;
      }

      // 5. PPT 讲解意图
      if (isPPTExplainCommand(clean)) {
        const cap = pptCapabilityRef.current;

        if (!cap) {
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '当前不在可讲解的PPT页面',
          }));
          return;
        }

        const candidates = cap.getModelCandidates();

        if (!candidates.length) {
          setState((prev) => ({
            ...prev,
            lastAssistantReply: '当前页面没有可选型号',
          }));
          return;
        }

        if (candidates.length === 1) {
          executePlayModel(candidates[0].id, candidates[0].label);
          return;
        }

        askForModelSelection(candidates);
        return;
      }

      // 6. chat scope 占位
      if (state.voiceSession?.scope === 'chat' && state.voiceSession.chatId) {
        setState((prev) => ({
          ...prev,
          lastAssistantReply: '好的，我已经收到你的问题',
        }));
        return;
      }

      // 7. 本地意图识别兜底
      if (useLocalIntent) {
        try {
          const intent = await detectLocalIntent({
            text: clean,
            api: localIntentApi,
            scene: buildLocalIntentScene(),
            signal: controller.signal,
          });

          if (!intent) {
            return;
          }
          const handled = await executeLocalIntent(intent, clean);
          if (handled) return;
        } catch (e: any) {
          if (e?.name === 'AbortError') {
            return;
          }

          throw e;
        } finally {
          if (handleTranscriptAbortRef.current === controller) {
            handleTranscriptAbortRef.current = null;
          }
        }
      }

      // 8. fallback
      setState((prev) => ({
        ...prev,
        lastAssistantReply:
          '这个操作我暂时还做不到。你可以试试打开首页、打开知识库，或者在PPT页面说讲解PPT',
      }));
    },
    [
      askForModelSelection,
      buildLocalIntentScene,
      executeLocalIntent,
      executePlayModel,
      handlePlaybackVoiceCommand,
      isPPTExplainCommand,
      localIntentApi,
      requestNavigate,
      resolveNavigateCommand,
      resolvePendingModelSelection,
      state.voiceSession,
      stopConversation,
      submitPPTQuestion,
      useLocalIntent,
    ],
  );

  const finishHoldToTalkPPTQuestion = useCallback(
    async (options?: { cancel?: boolean }) => {
      if (!holdToTalkActiveRef.current) return;

      holdToTalkActiveRef.current = false;
      shouldKeepListeningRef.current = false;
      const current = stateRef.current;
      const cancel = !!options?.cancel || holdToTalkCancelRef.current;
      const duration = Date.now() - holdToTalkStartedAtRef.current;

      clearTranscriptCommitTimer();

      try {
        recognizerRef.current?.stop?.();
      } catch {}

      const text =
        recognizerRef.current?.consumeFinalText?.({ includeInterim: true }) ||
        '';

      const clean = text.trim();

      setState((prev) => ({
        ...prev,
        listening: false,
        transcript: '',
      }));

      if (cancel) {
        try {
          recognizerRef.current?.consumeFinalText?.({ includeInterim: true });
        } catch {}

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          askingPptQuestion: false,
          awaitingPptQuestion: prev.pptQuestionMode
            ? true
            : prev.awaitingPptQuestion,
        }));

        message.info('已取消发送');
        return;
      }

      if (duration < 500) {
        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          askingPptQuestion: false,
        }));

        message.info('说话时间太短');
        return;
      }

      if (!clean) {
        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          askingPptQuestion: false,
        }));

        message.info('没有识别到内容');
        return;
      }

      const hasPendingModelSelection =
        current.pendingAction?.type === 'PLAY_PPT_MODEL' ||
        current.pendingAction?.type === 'INTRODUCE_PRODUCT';

      if (hasPendingModelSelection) {
        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
          lastAssistantReply: '正在识别你选择的型号...',
        }));

        await handleTranscript(clean);

        setState((prev) => ({
          ...prev,
          listening: false,
          transcript: '',
        }));

        return;
      }

      const shouldUsePptQuestion =
        current.mediaState.kind === 'ppt-video' || current.pptQuestionMode;

      if (shouldUsePptQuestion) {
        setState((prev) => ({
          ...prev,
          pendingAction: null,
          listening: false,
          transcript: '',
          askingPptQuestion: true,
          lastAssistantReply: '正在生成回答...',
        }));

        const playbackDecision = handlePlaybackVoiceCommand(clean);
        if (playbackDecision === 'handled') return;

        await submitPPTQuestion(clean);
        return;
      }

      if (
        current.pendingAction?.type === 'PLAY_PPT_MODEL' ||
        current.pendingAction?.type === 'INTRODUCE_PRODUCT'
      ) {
        await handleTranscript(clean);
        return;
      }

      if (hasPendingModelSelection) {
        await handleTranscript(clean);
        return;
      }

      await handleTranscript(clean);
    },
    [clearTranscriptCommitTimer, handleTranscript, submitPPTQuestion],
  );

  const cancelHoldToTalkPPTQuestion = useCallback(() => {
    if (!holdToTalkActiveRef.current) return;
    holdToTalkCancelRef.current = true;
  }, []);

  /** =========================
   * Main transcript handler
   * ========================= */

  function looksMeaningless(text: string) {
    const clean = text.trim();
    if (!clean) return true;

    const pure = clean.replace(
      /[，。！？,.!?；;：:\-\—_\s~、】【\[\]{}()<>/\\|]+/g,
      '',
    );
    if (!pure) return true;

    const weak = new Set([
      '啊',
      '哦',
      '呃',
      '嗯',
      '额',
      '唉',
      '哎',
      '欸',
      '嗯嗯',
    ]);
    if (weak.has(pure)) return true;

    return false;
  }

  const runLocalChat = async (text: string) => {
    const res = await fetch(`${management_api_host}/assistant/local-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });

    const data = await res.json();
    return (data.reply || '').trim();
  };

  handleTranscriptRef.current = handleTranscript;

  /** =========================
   * Start / wake
   * ========================= */

  const startConversation = useCallback(
    (opts: StartConversationOptions) => {
      const current = stateRef.current;

      const alreadyActive =
        current.mode === 'conversation' && !!current.voiceSession?.active;

      if (alreadyActive) {
        setState((prev) => ({
          ...prev,
          panelOpen: true,
        }));
        return;
      }

      const nextVoiceSession: VoiceSession = {
        active: true,
        mode: 'conversation',
        scope: opts.scope,
        owner: opts.scope === 'chat' ? 'assistant-chat' : 'assistant-global',
        chatId: opts.chatId,
        startedAt: Date.now(),
      };

      if (stateRef.current.mediaState.kind !== 'ppt-video') {
        openPanel();
      }
      stopAllVoice('restart-before-start');

      shouldKeepListeningRef.current = true;

      setState((prev) => ({
        ...prev,
        mode: 'conversation',
        voiceSession: nextVoiceSession,
        speaking: false,
        transcript: '',
        lastAssistantReply: '已开始监听，请直接说话',
        voiceListeningMode: 'free-question',
      }));

      // 用一个很短的异步，保证 setState 生效后再起 recognizer
      window.setTimeout(() => {
        startRecognizer();
      }, 0);
    },
    [openPanel, startRecognizer, stopAllVoice],
  );

  const enterWakeMode = useCallback(() => {
    const nextVoiceSession: VoiceSession = {
      active: true,
      mode: 'wake',
      scope: 'global',
      owner: 'assistant-global',
      startedAt: Date.now(),
    };

    openPanel();
    stopAllVoice('switch-to-wake-mode');

    setState((prev) => ({
      ...prev,
      mode: 'wake',
      voiceSession: nextVoiceSession,
      listening: false,
      speaking: false,
      transcript: '',
      voiceListeningMode: 'none',
    }));

    void speakText('好的，已进入唤醒模式');
  }, [openPanel, speakText, stopAllVoice]);

  const stopSpeakingOnly = useCallback(() => {
    muteCurrentAnswerPlaybackRef.current = true;
    streamSentenceBufferRef.current = '';
    speechQueueRef.current = [];
    speechQueueRunningRef.current = false;

    stopSpeech();

    setState((prev) => ({
      ...prev,
      speaking: false,
      askingPptQuestion: false,
      awaitingPptQuestion: true,
      transcript: '',
      pptChatHistory: prev.pptChatHistory.map((m) =>
        m.role === 'assistant' && m.status === 'streaming'
          ? { ...m, status: 'done' }
          : m,
      ),
    }));

    const current = stateRef.current;

    if (
      current.mediaState.kind === 'ppt-video' &&
      current.pptQuestionMode &&
      !current.listening
    ) {
      window.setTimeout(() => {
        forceStartListening();
      }, 400);
    }
  }, [forceStartListening, stopSpeech]);

  const clearPPTSession = useCallback(() => {
    stopListeningOnly();

    try {
      stopSpeakingOnly();
    } catch {}

    setState((prev) => ({
      ...prev,
      transcript: '',
      listening: false,
      speaking: false,

      pptQuestionMode: false,
      awaitingPptQuestion: false,
      askingPptQuestion: false,

      pendingAction: null,
      pendingQuestionModelId: undefined,
      pendingQuestionModelLabel: undefined,

      pptChatHistory: [],
    }));
  }, [stopListeningOnly, stopSpeakingOnly]);

  /** =========================
   * Page context
   * ========================= */

  const setPageContext = useCallback(
    (ctx: PageContext) => {
      setState((prev) => {
        const currentSession = prev.voiceSession;

        if (currentSession?.active && currentSession.scope === 'chat') {
          const stillSameChat =
            ctx.pageType === 'chat' &&
            !!ctx.chatId &&
            ctx.chatId === currentSession.chatId;

          if (!stillSameChat) {
            recognizerRef.current?.destroy?.();
            recognizerRef.current = null;
            stopSpeech();

            return {
              ...prev,
              pageContext: ctx,
              mode: 'idle',
              voiceSession: null,
              listening: false,
              speaking: false,
              transcript: '',
              pendingAction: null,
            };
          }
        }

        if (
          prev.pageContext.pathname === ctx.pathname &&
          prev.pageContext.pageType === ctx.pageType &&
          prev.pageContext.chatId === ctx.chatId
        ) {
          return prev;
        }

        return {
          ...prev,
          pageContext: ctx,
        };
      });
    },
    [stopSpeech],
  );

  /** =========================
   * Cleanup
   * ========================= */

  useEffect(() => {
    return () => {
      clearTranscriptCommitTimer();
      stopNavigationPolling();
      if (navigationAbortRef.current) {
        navigationAbortRef.current.abort();
      }
      try {
        recognizerRef.current?.destroy?.();
      } catch {}

      try {
        audioAbortRef.current?.abort();
      } catch {}

      const audio = audioRef.current;
      if (audio) {
        try {
          audio.pause();
          audio.src = '';
        } catch {}
      }

      if (currentAudioUrlRef.current) {
        try {
          URL.revokeObjectURL(currentAudioUrlRef.current);
        } catch {}
        currentAudioUrlRef.current = null;
      }
    };
  }, []);

  /** =========================
   * Value
   * ========================= */

  const value = useMemo<AssistantControllerApi>(
    () => ({
      state,
      startConversation,
      stopConversation,
      stopListeningOnly,
      enterWakeMode,
      stopAllVoice,
      handleTranscript,
      setPageContext,
      requestNavigate,
      bindNavigator,
      openPanel,
      closePanel,
      togglePanel,
      registerPPTCapability,
      registerPPTQuestionCapability,
      selectPendingModel,
      submitPPTQuestion,
      cancelPPTQuestionMode,
      setPPTPlaybackOpen,
      clearPPTPlayback,
      setVoiceListeningMode,
      openOverlay,
      toggleOverlay,
      enterPPTQuestionMode,
      stopSpeakingOnly,
      startHoldToTalkPPTQuestion,
      finishHoldToTalkPPTQuestion,
      cancelHoldToTalkPPTQuestion,

      clearPendingAction,
      clearPPTSession,

      navigateAndIntroduce,
      cancelNavigation,
    }),
    [
      state,
      startConversation,
      stopConversation,
      stopListeningOnly,
      enterWakeMode,
      stopAllVoice,
      handleTranscript,
      setPageContext,
      requestNavigate,
      bindNavigator,
      openPanel,
      closePanel,
      togglePanel,
      registerPPTCapability,
      registerPPTQuestionCapability,
      selectPendingModel,
      submitPPTQuestion,
      cancelPPTQuestionMode,
      setPPTPlaybackOpen,
      clearPPTPlayback,
      setVoiceListeningMode,
      openOverlay,
      toggleOverlay,
      enterPPTQuestionMode,
      stopSpeakingOnly,
      clearPendingAction,
      clearPPTSession,
      navigateAndIntroduce,
      cancelNavigation,
    ],
  );

  return (
    <AssistantControllerContext.Provider value={value}>
      {children}
    </AssistantControllerContext.Provider>
  );
}

/** =========================
 * Hook
 * ========================= */

export function useAssistantControllerContext() {
  const ctx = useContext(AssistantControllerContext);
  if (!ctx) {
    throw new Error(
      'useAssistantControllerContext must be used inside AssistantControllerProvider',
    );
  }
  return ctx;
}
